import { Link, useInRouterContext } from 'react-router-dom';
import { Home, RefreshCw, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ErrorPageProps {
  title?: string;
  message?: string;
  showHomeButton?: boolean;
  showRefreshButton?: boolean;
  showContactButton?: boolean;
}

export function ErrorPage({
  title = "Something went wrong",
  message = "We're sorry, but something unexpected happened. Please try again or contact support if the problem persists.",
  showHomeButton = true,
  showRefreshButton = true,
  showContactButton = true,
}: ErrorPageProps) {
  const inRouter = useInRouterContext();

  return (
    <div className="min-h-screen bg-white relative flex items-center justify-center p-4 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, #d1d5db 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />

      <div className="relative z-10 w-full max-w-lg">
        <div className="mx-auto bg-white/90 backdrop-blur border border-gray-200 shadow-xl rounded-2xl px-8 py-10 text-center space-y-4">
          <div className="flex justify-center mb-2">
            <img
              src="/images/doploy-logo.png"
              alt="Doploy"
              className="h-10 w-auto drop-shadow-sm"
            />
          </div>
          <div className="text-6xl font-bold text-gray-200">Oops!</div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {title}
          </h1>
          <div className="w-48 h-px bg-gray-200 mx-auto" />
          <p className="text-gray-600">
            {message}
          </p>
          <div className="flex flex-wrap justify-center gap-3 pt-2">
            {showHomeButton && (
              <Button
                className="gap-2"
                asChild
              >
                {inRouter ? (
                  <Link to="/projects">
                    <Home className="h-4 w-4" />
                    Go to Homepage
                  </Link>
                ) : (
                  <a href="/projects">
                    <Home className="h-4 w-4" />
                    Go to Homepage
                  </a>
                )}
              </Button>
            )}
            {showRefreshButton && (
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => window.location.reload()}
              >
                <RefreshCw className="h-4 w-4" />
                Try Again
              </Button>
            )}
            {showContactButton && (
              <Button
                variant="outline"
                className="gap-2"
                asChild
              >
                <a href="mailto:support@doploy.io">
                  <Mail className="h-4 w-4" />
                  Contact Support
                </a>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function NotFoundPage() {
  return (
    <ErrorPage
      title="Page not found"
      message="Oops, the page you're looking for does not exist. You may want to head back to the homepage."
      showRefreshButton={false}
      showContactButton={false}
    />
  );
}

export function ServerErrorPage() {
  return (
    <ErrorPage
      title="Server Error"
      message="We're experiencing some technical difficulties. Please try again in a few moments."
    />
  );
}

export { MaintenancePage } from './MaintenancePage';
